<template>
  <div class="home">
    <h2>Pipelines</h2>
    <b-container class="block">
      <b-form-input
        v-model="version"
        placeholder="Version (X.X.X)"
        md="3"
        class="version"
      />
      <b-form-select
        v-model="whitelabelId"
        class="whitelabel"
        :options="[
          { value: null, text: 'Select Whitelabel' },
          ...(this.$store.state.whitelabels.rows
            ? this.$store.state.whitelabels.rows.map(wl => ({
                value: wl.id,
                text: wl.name
              }))
            : {})
        ]"
      ></b-form-select>
      <b-button
        @click="runPipeline()"
        variant="primary"
        :disabled="!whitelabelId || !version"
        >Run pipeline</b-button
      >
      <b-button
        variant="dark"
        class="border border-light"
        :disabled="!whitelabelId"
        @click="
          $store.dispatch('reloadBuildsForWl', { whitelabelId }).catch(e => {})
        "
        ><span v-if="!$store.getters.isRequestPending('reloadBuildsForWl')"
          >Reload builds for wl</span
        >
        <span v-else>
          <b-spinner small type="grow" />
          Loading...
        </span></b-button
      >
      <b-button
        variant="dark"
        @click="$root.$emit('bv::show::modal', 'wlStatusesModal')"
        >Show reload statuses</b-button
      >
    </b-container>
    <b-container fluid>
      <b-row>
        <b-col md="12" class="my-1">
          <b-form-group label-cols-sm="1" label="Filter" class="mb-0">
            <b-input-group>
              <b-form-input
                v-model="filter.whitelabelName"
                placeholder="by Whitelabel"
              />
              <b-form-input v-model="filter.user" placeholder="by User" />
              <b-input-group-append>
                <b-button
                  :disabled="!filter.whitelabelName && !filter.user"
                  @click="
                    filter = {};
                    forceFilter();
                  "
                  >Clear</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        ref="pipelinesTable"
      >
        <template>
          <b-button size="sm" class="m-1" variant="info">…</b-button>
          <b-button size="sm" class="m-1" variant="warning">✎</b-button>
          <b-button size="sm" variant="danger" class="m-1 ml-3">X</b-button>
        </template>
        <template v-slot:cell(webUrl)="row">
          <a :href="row.item.webUrl" target="_blank">{{
            row.item.webUrl
          }}</a></template
        ><template v-slot:cell(whitelabel)="row">
          {{ row.item.whitelabel ? row.item.whitelabel.name : '' }}
        </template>

        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            @click="
              deletePipeline({
                target: $event.target,
                id: row.item.id
              })
            "
          >
            X
          </b-button>
        </template>
      </b-table>

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>

    <b-modal id="wlStatusesModal" title="Update statuses by WL" ok-only>
      <div v-if="Object.keys($store.state.ws.wlStatuses).length > 0">
        <div
          v-for="(value, index) in $store.state.ws.wlStatuses"
          v-b-popover.hover="value.join('\n')"
          class="wlStatusRow"
        >
          <span class="wlName">{{ index }}</span>
          {{ value[value.length - 1] }}
        </div>
      </div>
      <div v-else>
        No update info. Maybe update was never started since app launch?
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: {
        whitelabelName: '',
        user: ''
      },
      form: {
        id: null,
        pipelineId: null,
        whitelabelId: null,
        status: null,
        webUrl: null
      },
      version: '',
      whitelabelId: '',
      requestTimeout: null
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'pipelineId',
          label: 'pipelineId',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'ref',
          label: 'version',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'status',
          label: 'status',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'webUrl',
          label: 'webUrl',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'whitelabel',
          label: 'whitelabel',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'user',
          label: 'user',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'createdAt',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.pipelines.count;
    }
  },
  methods: {
    items(ctx) {
      clearTimeout(this.requestTimeout);
      if (!this.canRequest) {
        this.requestTimeout = setTimeout(() => {
          this.canRequest = true;
          this.$refs.pipelinesTable.refresh();
        }, 1000);
        return this.getPreparedItems();
      }

      this.canRequest = false;
      const currentFilter = Object.keys(this.filter).reduce(
        (acc, curr) =>
          this.filter[curr] ? { ...acc, [curr]: this.filter[curr] } : acc,
        {}
      );

      const promise = this.$store.dispatch('getPipelines', {
        ctx,
        filter: currentFilter
      });

      return promise.then(this.getPreparedItems).catch(this.getPreparedItems);
    },
    getPreparedItems() {
      const items = this.$store.state.pipelines.rows?.map(pipeline => {
        if (pipeline.status === 'success') {
          pipeline._rowVariant = 'success';
        }
        if (pipeline.status === 'pending' || pipeline.status === 'running') {
          pipeline._rowVariant = 'warning';
        }
        if (pipeline.status === 'skipped') {
          pipeline._rowVariant = 'info';
        }
        if (pipeline.status === 'failed' || pipeline.status === 'canceled') {
          pipeline._rowVariant = 'danger';
        }
        return pipeline;
      });

      return items || [];
    },
    runPipeline() {
      if (!this.version || !this.whitelabelId) {
        return;
      }

      const promise = this.$store.dispatch('runPipeline', {
        version: this.version,
        whitelabelId: this.whitelabelId
      });
      promise.then(this.$refs.pipelinesTable.refresh);
    },
    getWhitelabels() {
      const promise = this.$store.dispatch('getWhitelabels', {
        sortDesc: false,
        currentPage: 1,
        perPage: 1000
      });
      return promise.then(() => {});
    },
    forceFilter() {
      this.onFiltered();
      this.$refs.pipelinesTable.refresh();
    },
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    deletePipeline(id) {
      const promise = this.$store.dispatch('deletePipeline', { id });
      return promise.then(() => {
        this.$refs.pipelinesTable.refresh();
      });
    }
  },
  created: function() {
    this.getWhitelabels();
  }
};
</script>

<style scoped>
.block {
  display: inline-block;
  padding: 5px;
  margin-left: 5px;
}

.version {
  display: inline-block;
  width: 165px;
  margin-right: 5px;
}

.whitelabel {
  display: inline-block;
  width: 165px;
  margin-right: 5px;
}

.wlName {
  font-weight: bold;
}
</style>
